import * as React from 'react';
import { Checkbox, IconButton, Spinner, Table } from '@ixicare/ui';
import { Download, RefreshCw } from 'lucide-react';
import { t } from '~i18n';
import styles from './GenericTable.module.css';
import { ColumnManager } from '../ColumnManager/ColumnManager';
import { ColumnDefinition, useColumnManager } from '../ColumnManager/useColumnManager';
import { BaseTableConfig, VisibleCol, WithColumnManager, WithExport } from './types';
import { useExport } from './hooks/useExport';
import { useRowSelection } from './hooks/useRowSelection';
import { SelectionActionBar, type ActionProps } from './SelectionActionBar';

interface Props {
  // The row type is opaque to GenericTable — rows are pre-transformed by the
  // caller, and the config's sort/csv accessor maps are keyed by columnId.
  config: BaseTableConfig & Partial<WithColumnManager<any>> & Partial<WithExport<any>>;
  orderBy?: string;
  rows: (string | number | undefined | null | React.ReactElement | any)[][];
  onClickColumn?: (columnId: string) => void;
  asc?: boolean;
  actions?: ActionProps[];
  onActionClick?: (rowIndices: number[], action: string) => void;
  isActionVisible?: (rowIndex: number, action: string) => boolean;
  getRowLabel?: (rowIndex: number) => React.ReactNode;
  // Stable identity per row. With it, selection survives re-sorts/refetches and
  // can be persisted by a parent; without it, selection falls back to row index.
  getRowKey?: (rowIndex: number) => string;
  // Controlled selection: pass both to let a parent own the selected-row keys.
  selectedKeys?: Set<string>;
  onSelectedKeysChange?: (next: Set<string>) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  csvData?: any[];
  csvAccessors?: Record<string, (item: any) => string | number | null | undefined>;
  csvFilename?: string;
  onPrepareExport?: () => Promise<void>;
}

const SELECT_ID = '__select__';

export const GenericTable: React.FC<Props> = ({
  config,
  orderBy,
  rows,
  onClickColumn,
  asc,
  actions,
  onActionClick,
  isActionVisible,
  getRowLabel,
  getRowKey,
  selectedKeys,
  onSelectedKeysChange,
  onRefresh,
  isRefreshing,
  csvData,
  csvAccessors,
  csvFilename,
  onPrepareExport,
}) => {
  const tableId = config.tableId;
  const managerEnabled = !!(tableId && config.columnIds && config.columnIds.length === config.columns.length);

  const csvEnabled = !!(csvData && csvAccessors);
  const showToolbar = managerEnabled || !!onRefresh || csvEnabled;

  const columnDefinitions: {
    allColumns: ColumnDefinition[];
    defaultColumns: ColumnDefinition[];
  } = React.useMemo(() => {
    if (!managerEnabled) return { allColumns: [], defaultColumns: [] };

    // Asserting columnIds exists, see managerEnabled
    const allColumns = config.columnIds!.map((id, i) => ({
      id,
      label: config.columns[i],
    }));

    const defaultColumns = config.defaultColumnIds
      ? allColumns.filter((col) => config.defaultColumnIds!.includes(col.id))
      : allColumns;

    return {
      allColumns,
      defaultColumns,
    };
  }, [managerEnabled, config.columnIds, config.columns, config.defaultColumnIds]);

  const {
    columns: columnState,
    setColumns,
    toggleColumn,
    hideColumn,
    setAll,
    resetColumns,
    isDirty,
  } = useColumnManager({
    tableId: managerEnabled ? tableId : undefined,
    ...columnDefinitions,
  });

  const [popoverOpen, setPopoverOpen] = React.useState(false);

  const selection = useRowSelection({ rows, getRowKey, selectedKeys, onSelectedKeysChange });

  const indexById = React.useMemo(() => {
    const m = new Map<string, number>();
    if (managerEnabled) config.columnIds!.forEach((id, i) => m.set(id, i));
    return m;
  }, [managerEnabled, config.columnIds]);

  const visibleCols: VisibleCol[] = React.useMemo(() => {
    const allowedCols: VisibleCol[] = config.columns.map((label, i) => ({
      id: String(i),
      label,
      originalIndex: i,
    }));

    const dataCols = !managerEnabled
      ? allowedCols
      : columnState
          .filter((c) => c.visible)
          .map((c) => {
            const idx = indexById.get(c.id);
            if (idx === undefined) return null;
            return { id: c.id, label: config.columns[idx], originalIndex: idx };
          })
          .filter((x): x is VisibleCol => x !== null);

    const selectionCol = {
      id: SELECT_ID,
      label: '',
      originalIndex: -1,
    };

    return config.rowsSelectable ? [selectionCol, ...dataCols] : dataCols;
  }, [managerEnabled, columnState, config, indexById]);

  const visibleOrderColumnIndex = orderBy ? visibleCols.findIndex((v) => v.id === orderBy) : -1;

  const handleClickColumn = onClickColumn ? (visIdx: number) => onClickColumn(visibleCols[visIdx].id) : undefined;

  const handleExport = useExport({
    csvEnabled,
    csvData,
    csvAccessors,
    csvFilename,
    tableId,
    visibleCols,
  });

  const [isExporting, setIsExporting] = React.useState(false);

  const onExportClick = async () => {
    setIsExporting(true);
    try {
      // Wait until loadAll resolved
      if (onPrepareExport) await onPrepareExport();
      handleExport();
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      {showToolbar && (
        <div className={styles.Toolbar}>
          {(isRefreshing || isExporting) && <Spinner size="small" label={null} />}
          {onRefresh && (
            <button
              type="button"
              className={`${styles.ToolbarButton}`}
              onClick={onRefresh}
              disabled={isRefreshing || isExporting}
              aria-label={t('genericTable.refreshAria')}
              title={t('genericTable.refreshAria')}
            >
              <RefreshCw size="0.85em" strokeWidth={2} />
            </button>
          )}
          {csvEnabled && (
            <button
              type="button"
              className={styles.ToolbarButton}
              onClick={onExportClick}
              disabled={isRefreshing || isExporting}
              aria-label={t('genericTable.exportAria')}
              title={t('genericTable.exportTitle')}
            >
              <Download size="0.85em" strokeWidth={2} />
            </button>
          )}
          {managerEnabled && (
            <>
              <button
                type="button"
                data-cm-toggle
                className={`${styles.ToolbarButton} ${popoverOpen ? styles.ToolbarButtonActive : ''}`}
                onClick={() => setPopoverOpen((v) => !v)}
                aria-label={t('columnManager.toggleAria')}
                aria-expanded={popoverOpen}
                title={t('columnManager.toggleAria')}
              >
                <i className="icon-settings" />
              </button>
              {popoverOpen && (
                <ColumnManager
                  columns={columnState}
                  allColumns={columnDefinitions.allColumns}
                  isDirty={isDirty}
                  onChange={setColumns}
                  onToggle={toggleColumn}
                  onSetAll={setAll}
                  onReset={resetColumns}
                  onClose={() => setPopoverOpen(false)}
                />
              )}
            </>
          )}
        </div>
      )}
      <Table>
        <Table.Head>
          <Table.Row>
            {visibleCols.map((col, i) => {
              if (col.id === SELECT_ID) {
                return (
                  <Table.HeadCol key={col.id} className={styles.SelectCol}>
                    <Checkbox
                      id="select-all"
                      name="select-all"
                      checked={selection.allSelected}
                      label={' '}
                      onChange={selection.toggleAll}
                    />
                  </Table.HeadCol>
                );
              }
              return (
                <Table.HeadCol
                  onClick={handleClickColumn ? () => handleClickColumn(i) : undefined}
                  key={col.id}
                  data-column-name={col.label.toLowerCase().replace(/\s+/g, '-')}
                >
                  <span className={styles.HeadColInner}>
                    <span>{col.label}</span>
                    <i
                      className={`${styles.SortChevron} ${
                        visibleOrderColumnIndex === i ? (asc ? 'icon-chevron-up' : 'icon-chevron-down') : ''
                      }`}
                    />
                    {managerEnabled && (
                      <span
                        className={styles.HeadX}
                        onClick={(e) => {
                          e.stopPropagation();
                          hideColumn(col.id);
                        }}
                        title={t('columnManager.hideColumn', { label: col.label })}
                        role="button"
                      >
                        <i className="icon-cross" />
                      </span>
                    )}
                  </span>
                </Table.HeadCol>
              );
            })}
            {selection.count === 0 && !!actions?.length && <Table.HeadCol aria-label={t('genericTable.actionsAria')} />}
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {rows.map((row, i) => (
            <Table.Row key={i}>
              {visibleCols.map((col, j) => {
                if (col.id === SELECT_ID) {
                  return (
                    <Table.Col key={j} className={styles.SelectCol}>
                      <Checkbox
                        id={`select-${i}`}
                        name={`select-${i}`}
                        checked={selection.isSelected(i)}
                        label={' '}
                        onChange={() => selection.toggleRow(i)}
                      />
                    </Table.Col>
                  );
                }
                const value = row[col.originalIndex];
                return (
                  <Table.Col key={j} data-column-name={col.label.toLowerCase().replace(/\s+/g, '-')}>
                    {col.label === 'Resident name'
                      ? value === undefined
                        ? '-'
                        : value?.firstName + ' ' + value?.lastName
                      : (value ?? '')}
                  </Table.Col>
                );
              })}
              {selection.count === 0 && !!actions?.length && (
                <Table.Col className={styles.ActionsCol}>
                  <span className={styles.ActionsCellInner}>
                    {actions.map((a) =>
                      isActionVisible?.(i, a.icon) !== false ? (
                        <span key={a.icon} title={a.label || undefined}>
                          <IconButton
                            icon={a.lucide ? undefined : a.icon}
                            lucide={a.lucide}
                            smallIcon={false}
                            onClick={() => onActionClick?.([i], a.icon)}
                          />
                        </span>
                      ) : null
                    )}
                  </span>
                </Table.Col>
              )}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {selection.count > 0 && !!actions?.length && (
        <SelectionActionBar
          count={selection.count}
          selectedIndices={selection.selectedIndices}
          actions={actions}
          onActionClick={onActionClick}
          isActionVisible={isActionVisible}
          getRowLabel={getRowLabel}
          onClear={selection.clear}
        />
      )}
    </>
  );
};
